export { useProductStore };
import { defineStore } from 'pinia';
import { kyWithAuth } from '../libs/ky';
import { queryStringify } from '../libs/utils.js';

const initialState = () => ({
  products: [],
  collections: [],
  catalog: [],
  product: {},
  variant: {},
  options: {},
  currentPage: 1,
  pageSize: 0,
  hasNextPage: false,
  searchQuery: null,
  activeCategory: null,
  error: null,
  loading: false
});

const useProductStore = defineStore('products', {
  state: initialState,
  getters: {
    getSortedProducts: (state) => {
      return state.products;
    }
  },
  actions: {
    setSearchQuery(query) {
      this.searchQuery = query;
      this.fetchProducts({ page: 1, clearCategory: true });
    },
    setPage(page) {
      this.fetchProducts({ page });
    },
    async fetchProducts(options = {}) {
      this.loading = true;
      this.error = null;

      const {
        page = this.currentPage,
        pageSize = this.pageSize || 30,
        catalog = false,
        conditions = undefined,
        products: productIds = undefined,
        category,
        priceRange = undefined,
        seller = undefined,
        shop = false,
        clearCategory = false
      } = options;

      if (clearCategory) {
        this.activeCategory = null;
      } else if (category !== undefined) {
        this.activeCategory = category;
      }

      this.currentPage = page;

      const apiParams = {
        page: this.currentPage,
        limit: pageSize,
        shop
      };

      if (this.searchQuery) {
        apiParams.search = this.searchQuery;
      }
      if (productIds) {
        apiParams.products = productIds;
      }
      if (this.activeCategory) {
        apiParams.category = this.activeCategory;
      }
      if (conditions) {
        apiParams.conditions = conditions;
      }
      if (priceRange && priceRange.min !== undefined) {
        apiParams['priceRange.min'] = priceRange.min;
      }
      if (priceRange && priceRange.max !== undefined) {
        apiParams['priceRange.max'] = priceRange.max;
      }
      if (seller) {
        apiParams.seller = seller;
      }

      const searchParams = queryStringify(apiParams);
      try {
        const endpoint = catalog ? 'products/catalog' : 'products';
        const response = await kyWithAuth.get(endpoint, { searchParams }).json();

        if (catalog) {
          const { shop: shopData = {} } = await kyWithAuth.get('shops').json();
          const productsWithDetails = response.products.map((p) => {
            const prices = p.variants.map((v) => v.price);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            const shopProducts = shopData.products || [];
            const isInShop = shopProducts.some((sp) => sp.product_id === p._id);
            return {
              ...p,
              included: !!isInShop,
              minPrice,
              maxPrice
            };
          });
          this.catalog = productsWithDetails;
        } else {
          this.products = response.products;
        }
        this.currentPage = response.currentPage;
        this.hasNextPage = response.hasNextPage;
        this.pageSize = pageSize;
        if (process.env.NODE_ENV === 'development') {
          console.log(
            'products.js fetchProducts - Category used for API:',
            apiParams.category,
            '; Store activeCategory:',
            this.activeCategory,
            '; Options received:',
            options
          );
          console.log(catalog ? 'catalog:' : 'products:', response.products);
        }
      } catch (e) {
        console.error('Error fetching products:', e);
        this.error = 'Failed to fetch products. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
    async fetchCollectionProducts({ type, payload, page = 1, pageSize = 25 }) {
      this.loading = true;
      try {
        let queryParams = { page, pageSize };

        if (type === 'manual') {
          queryParams.products = payload;
        } else if (type === 'automated') {
          queryParams.conditions = payload.map(
            (condition) => `${condition.field}__${condition.condition}:${condition.value}`
          );
        }
        await this.fetchProducts(queryParams);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async fetchProduct(productId) {
      this.loading = true;
      try {
        const { product: fetchedProduct = {} } = await kyWithAuth
          .get(`products/${productId}`)
          .json();
        this.product = fetchedProduct;
        const activeVariants =
          fetchedProduct.variants?.filter((variant) => variant.status !== 'deleted') || [];
        if (activeVariants.length > 0) {
          this.variant = activeVariants[0]; // Sélectionner la première variante active
        } else {
          this.variant = {}; // Aucune variante active
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchProductByCategory(categoryName) {
      try {
        await this.fetchProducts({ category: categoryName, page: 1 });
        return this.products;
      } catch (error) {
        console.error('Error fetching product by category:', error);
        return [];
      }
    },
    async fetchShop() {
      this.loading = true;
      try {
        const { shop: shopData = {} } = await kyWithAuth.get('shops').json();
        this.shop = shopData?.products || [];
      } catch (error) {
        console.error('Error fetching shop:', error);
      } finally {
        this.loading = false;
      }
    },
    async clearProduct() {
      this.product = {};
      this.variant = {};
      this.options = {};
    },
    async deleteProduct(productId) {
      try {
        await kyWithAuth.delete(`products/${productId}`);
        this.products = this.products.filter((product) => product._id !== productId);
      } catch (e) {
        console.error(e);
      }
    },
    async addToShop(productId) {
      try {
        const foundProduct = this.catalog.find((product) => product._id === productId);
        if (foundProduct) {
          await kyWithAuth.patch(`shops/products/add/${productId}`, {
            json: { catalog_id: foundProduct.catalog_id }
          });
          foundProduct.included = true;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async removeFromShop(productId) {
      try {
        const foundProduct = this.catalog.find((product) => product._id === productId);
        if (foundProduct) {
          await kyWithAuth.patch(`shops/products/remove/${productId}`, {
            json: { catalog_id: foundProduct.catalog_id }
          });
          foundProduct.included = false;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async importProducts(formData) {
      this.loading = true;
      this.error = null;
      try {
        const response = await kyWithAuth
          .post('products/import', {
            body: formData
          })
          .json();
        if (process.env.NODE_ENV === 'development') {
          console.log('Import response:', response);
        }
        return response;
      } catch (error) {
        console.error('Error importing products:', error);
        this.error = 'Failed to import products. Please try again later.';
        throw error;
      } finally {
        this.loading = false;
      }
    },
    clearActiveCategoryFilter() {
      if (this.activeCategory !== null) {
        this.activeCategory = null;
      }
    },
    reset() {
      const s = initialState();
      Object.keys(s).forEach((key) => {
        this[key] = s[key];
      });
      console.log('Product store has been reset.');
    }
  }
});
